// src/components/Dashboard_ADM/Atendimento/MetricasTab.tsx
// Painel de métricas — seção 8.5. GET /whatsapp-agent/metrics

import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, Loader2, RefreshCw, Clock, AlertTriangle, TrendingUp } from 'lucide-react';
import apiClient from '../../../services/api';
import { ConversationAgentId, AGENT_LABELS, NICHO_AGENTS } from './shared';

interface Metrics {
  totalConversas: number;
  pendentes: number;
  emAtendimento: number;
  escaladas: number;
  taxaEscalonamento: number;
  conversasPorNicho: Partial<Record<ConversationAgentId, number>>;
  tempoMedioPrimeiraRespostaMinutos: number | null;
  totalLeads: number;
  leadsAtivos: number;
  taxaConversao: number;
}

function formatMinutos(min: number | null): string {
  if (min === null) return '—';
  if (min < 1) return '< 1 min';
  if (min < 60) return `${min.toFixed(1)} min`;
  return `${(min / 60).toFixed(1)} h`;
}

const MetricasTab: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get<Metrics>('/whatsapp-agent/metrics');
      setMetrics(data);
    } catch {
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const maxNicho = metrics
    ? Math.max(1, ...NICHO_AGENTS.map((id) => metrics.conversasPorNicho[id] ?? 0))
    : 1;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1 flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-blue-600" /> Métricas
            </h2>
            <p className="text-gray-600">Painel de acompanhamento do agente (seção 8.5)</p>
          </div>
          <button onClick={fetchMetrics}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-blue-600 animate-spin" /></div>
      ) : !metrics ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-500">
          Não foi possível carregar as métricas
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 text-gray-500 text-xs font-bold mb-2">
                <Clock className="w-4 h-4" /> TEMPO MÉDIO 1ª RESPOSTA
              </div>
              <p className="text-2xl font-black text-gray-900">{formatMinutos(metrics.tempoMedioPrimeiraRespostaMinutos)}</p>
              <p className="text-xs text-gray-400 mt-1">Meta: menos de 1 minuto</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 text-gray-500 text-xs font-bold mb-2">
                <TrendingUp className="w-4 h-4" /> TAXA DE CONVERSÃO
              </div>
              <p className="text-2xl font-black text-gray-900">{metrics.taxaConversao.toFixed(1)}%</p>
              <p className="text-xs text-gray-400 mt-1">{metrics.leadsAtivos} de {metrics.totalLeads} leads viraram filiados</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 text-gray-500 text-xs font-bold mb-2">
                <AlertTriangle className="w-4 h-4" /> TAXA DE ESCALONAMENTO
              </div>
              <p className="text-2xl font-black text-gray-900">{metrics.taxaEscalonamento.toFixed(1)}%</p>
              <p className="text-xs text-gray-400 mt-1">{metrics.escaladas} de {metrics.totalConversas} conversas</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 text-gray-500 text-xs font-bold mb-2">
                <BarChart3 className="w-4 h-4" /> CONVERSAS ATIVAS
              </div>
              <p className="text-2xl font-black text-gray-900">{metrics.emAtendimento}</p>
              <p className="text-xs text-gray-400 mt-1">{metrics.pendentes} pendentes</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-black text-gray-900 mb-4">Conversas por nicho</h3>
            <div className="space-y-3">
              {NICHO_AGENTS.map((id) => {
                const valor = metrics.conversasPorNicho[id] ?? 0;
                const pct = (valor / maxNicho) * 100;
                return (
                  <div key={id}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-bold text-gray-700">{AGENT_LABELS[id]}</span>
                      <span className="font-black text-gray-900">{valor}</span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-600 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MetricasTab;
